import type { Locale } from './locale';
import { formatAmount } from './money';

/**
 * People-facing messages for the API's stable problem codes and field-error
 * codes (RFC 9457 problem+json). Unknown codes fall back to the localized
 * `title` the API sends, then to a generic message.
 *
 * This catalog holds the codes both apps can meet; each app registers its own
 * (src/problems/*) where its pages load. A key "code.variant" is a message for
 * the variant the problem's params select (see variantOf).
 */
type Text = { ar: string; en: string };

/** A catalog of messages by code, as the apps register them. */
export type ProblemTexts = Readonly<Record<string, Text>>;

const problems: Record<string, Text> = {
  sold_out: { ar: 'لا توجد سيارة متاحة في هذا الموعد', en: 'No car is available at this time' },
  day_closed: { ar: 'هذا اليوم مغلق للحجز', en: 'This day is closed for bookings' },
  extra_sold_out: { ar: 'نفدت هذه الإضافة في هذا الموعد', en: 'This add-on is sold out for this time' },
  model_unavailable: { ar: 'هذه السيارة غير متاحة للحجز', en: 'This car cannot be booked' },
  hold_expired: {
    ar: 'انتهت مهلة الدفع، والسيارة لم تعد محجوزة لك',
    en: 'The payment time ran out and the car is no longer held for you',
  },
  version_conflict: {
    ar: 'غيّر شخص آخر هذه البيانات قبل قليل. حدّث الصفحة وحاول مجدداً.',
    en: 'Someone else changed this a moment ago. Refresh and try again.',
  },
  precondition_failed: {
    ar: 'غيّر شخص آخر هذه البيانات قبل قليل. حدّث الصفحة وحاول مجدداً.',
    en: 'Someone else changed this a moment ago. Refresh and try again.',
  },
  rate_limited: {
    ar: 'محاولات كثيرة. انتظر قليلاً ثم حاول مجدداً.',
    en: 'Too many attempts. Wait a little and try again.',
  },
  unauthorized: {
    ar: 'انتهت الجلسة. سجّل الدخول مجدداً.',
    en: 'Your session has ended. Please sign in again.',
  },
  permission_denied: { ar: 'لا تملك صلاحية هذا الإجراء', en: "You don't have permission to do this" },
  forbidden: { ar: 'لا تملك صلاحية هذا الإجراء', en: "You don't have permission to do this" },
  not_found: { ar: 'غير موجود', en: 'Not found' },
  booking_not_found: { ar: 'لم نجد هذا الحجز', en: "We couldn't find this booking" },
  promo_exhausted: { ar: 'انتهت استخدامات كود الخصم', en: 'This promo code has been used up' },
  promo_customer_limit: { ar: 'استخدمت هذا الكود من قبل', en: "You've already used this code" },
  promo_not_found: { ar: 'كود الخصم غير صالح', en: 'This promo code is not valid' },
  credit_insufficient: { ar: 'الرصيد غير كافٍ', en: 'Not enough credit' },
  credit_request_pending: {
    ar: 'يوجد طلب تحويل قيد المراجعة لهذا الحجز',
    en: 'A credit request for this booking is already being reviewed',
  },
  refund_window_passed: {
    ar: 'بقي أقل من 3 أيام على موعد الاستلام. حسب سياسة الإيجار لا يُلغى الحجز ولا يُحوّل إلى رصيد.',
    en: 'Less than 3 days remain before pickup. Under the rental policy the booking cannot be cancelled or converted to credit.',
  },
  otp_code: { ar: 'الرمز غير صحيح أو انتهت صلاحيته', en: 'The code is wrong or has expired' },
  online_payments_disabled: { ar: 'الدفع الإلكتروني متوقف مؤقتاً', en: 'Online payment is paused for now' },
  payment_exceeds_balance: {
    ar: 'المبلغ أكبر من المتبقي على الحجز',
    en: 'The amount is more than the balance due',
  },
  nothing_to_collect: { ar: 'لا يوجد مبلغ متبقٍ للتحصيل', en: 'There is nothing left to collect' },
  handover_too_early: {
    ar: 'لا يمكن التسليم قبل موعد الاستلام',
    en: 'The car cannot be handed over before the pickup time',
  },
  car_not_ready: { ar: 'السيارة غير جاهزة للتسليم', en: 'The car is not ready for handover' },
  car_unavailable: { ar: 'السيارة غير متاحة في هذا الموعد', en: 'The car is not available at this time' },
  role_in_use: { ar: 'لا يمكن حذف دور مسند لأعضاء', en: "A role that members still hold can't be deleted" },
  role_locked: { ar: 'هذا الدور مقفل', en: 'This role is locked' },
  username_taken: { ar: 'اسم المستخدم مستخدم', en: 'This username is taken' },
  invite_expired: { ar: 'انتهت صلاحية رابط الدعوة', en: 'This invite link has expired' },
  invite_used: { ar: 'استُخدم رابط الدعوة من قبل', en: 'This invite link has already been used' },
  invite_not_found: { ar: 'رابط الدعوة غير صالح', en: 'This invite link is not valid' },
  invite_revoked: { ar: 'أُلغي رابط الدعوة', en: 'This invite link was revoked' },
  partial_refund_unsupported: {
    ar: 'استرداد البطاقة يكون للدفعة كاملة. للاسترداد الجزئي استخدم النقد عند المكتب.',
    en: 'Card refunds return the whole payment. Use cash at the desk for a partial refund.',
  },
  csrf_rejected: { ar: 'رُفض الطلب لأسباب أمنية', en: 'The request was rejected for security reasons' },
  payload_too_large: { ar: 'الملف كبير جداً', en: 'The file is too large' },
  unsupported_media_type: { ar: 'نوع الملف غير مدعوم', en: 'This file type is not supported' },
  idempotency_key_reused: {
    ar: 'أُرسل هذا الطلب من قبل ببيانات مختلفة',
    en: 'This request was already sent with different data',
  },
  in_progress: { ar: 'الطلب قيد التنفيذ، انتظر لحظة', en: 'This request is still being processed' },
  idempotency_result_unavailable: {
    ar: 'نُفّذ هذا الطلب من قبل. حدّث الصفحة لترى النتيجة.',
    en: 'This request was already carried out. Refresh the page to see the result.',
  },
  network: {
    ar: 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجدداً.',
    en: "Couldn't connect. Check your connection and try again.",
  },
  internal: { ar: 'حدث خطأ غير متوقع. حاول مجدداً.', en: 'Something went wrong. Please try again.' },
  unavailable: {
    ar: 'الخدمة غير متاحة الآن. حاول بعد قليل.',
    en: 'The service is unavailable. Try again shortly.',
  },
  database_busy: {
    ar: 'النظام مشغول الآن. حاول مجدداً بعد لحظات.',
    en: 'The system is busy right now. Try again in a moment.',
  },
  timeout: {
    ar: 'استغرق الطلب وقتاً أطول من اللازم. حاول مجدداً.',
    en: 'The request took too long. Try again.',
  },
  // A request the page built wrongly, or a page older than the server: reloading fixes both.
  bad_request: {
    ar: 'تعذّر تنفيذ الطلب. حدّث الصفحة وحاول مجدداً.',
    en: "The request couldn't be processed. Refresh the page and try again.",
  },
  body_required: {
    ar: 'تعذّر تنفيذ الطلب. حدّث الصفحة وحاول مجدداً.',
    en: "The request couldn't be processed. Refresh the page and try again.",
  },
  idempotency_key_required: {
    ar: 'تعذّر تنفيذ الطلب. حدّث الصفحة وحاول مجدداً.',
    en: "The request couldn't be processed. Refresh the page and try again.",
  },
  idempotency_key_invalid: {
    ar: 'تعذّر تنفيذ الطلب. حدّث الصفحة وحاول مجدداً.',
    en: "The request couldn't be processed. Refresh the page and try again.",
  },
  precondition_required: {
    ar: 'تعذّر الحفظ. حدّث الصفحة وحاول مجدداً.',
    en: "This couldn't be saved. Refresh the page and try again.",
  },
  route_not_found: {
    ar: 'هذه الخدمة غير متاحة. حدّث الصفحة.',
    en: 'This is not available. Refresh the page.',
  },
  method_not_allowed: {
    ar: 'هذه الخدمة غير متاحة. حدّث الصفحة.',
    en: 'This is not available. Refresh the page.',
  },
  cursor_invalid: { ar: 'تعذّر تحميل المزيد. حدّث الصفحة.', en: "Couldn't load more. Refresh the page." },
  cursor_mismatch: { ar: 'تعذّر تحميل المزيد. حدّث الصفحة.', en: "Couldn't load more. Refresh the page." },
  conflict: {
    ar: 'تغيّرت البيانات ولم يعد هذا ممكناً. حدّث الصفحة.',
    en: 'The data changed and this is no longer possible. Refresh the page.',
  },
  feature_unavailable: { ar: 'هذه الخدمة غير متاحة بعد', en: 'This is not available yet' },
  phone_invalid: { ar: 'رقم الهاتف غير صحيح', en: 'The phone number is not valid' },
  phone_not_mobile: { ar: 'أدخل رقم هاتف نقّال', en: 'Enter a mobile number' },
  customer_name_invalid: {
    ar: 'الاسم فيه رموز غير مسموحة',
    en: 'The name has characters that are not allowed',
  },
  customer_name_too_long: { ar: 'الاسم طويل جداً', en: 'The name is too long' },
  civil_id_invalid: { ar: 'رقم البطاقة المدنية غير صحيح', en: 'The civil ID number is not valid' },
  locale_invalid: { ar: 'هذه اللغة غير مدعومة', en: 'This language is not supported' },
  schedule_invalid: { ar: 'الموعد خارج أوقات الحجز', en: 'This time is outside the booking hours' },
  pickup_in_past: {
    ar: 'موعد الاستلام مضى. اختر موعداً لاحقاً.',
    en: 'The pickup time has passed. Choose a later time.',
  },
  range_invalid: { ar: 'نطاق التاريخ غير صحيح', en: 'The date range is not valid' },
  period_invalid: { ar: 'يجب أن تأتي النهاية بعد البداية', en: 'The end must come after the start' },
  model_not_found: { ar: 'لم نجد هذه السيارة', en: "We couldn't find this car" },
  extra_not_found: { ar: 'لم نجد هذه الإضافة', en: "We couldn't find this add-on" },
  extra_unavailable: {
    ar: 'إحدى الإضافات غير متاحة الآن',
    en: 'One of the add-ons is not available right now',
  },
  extra_quantity: {
    ar: 'الكمية المطلوبة من إحدى الإضافات غير متوفرة',
    en: 'Not enough of one of the add-ons is available',
  },
  promo_already_redeemed: {
    ar: 'استُخدم كود خصم في هذا الحجز من قبل',
    en: 'This booking already used a promo code',
  },
  documents_invalid: {
    ar: 'تعذّر استخدام الصور المرفوعة. ارفعها مجدداً.',
    en: "The uploaded photos can't be used. Upload them again.",
  },
  upload_kind_invalid: { ar: 'نوع المستند غير صحيح', en: 'The document type is not valid' },
  upload_size_invalid: {
    ar: 'تعذّر رفع الملف. حاول مجدداً.',
    en: "The file couldn't be uploaded. Try again.",
  },
  credit_unavailable: {
    ar: 'الرصيد غير متاح الآن. حاول بعد قليل.',
    en: 'Credit is not available right now. Try again shortly.',
  },
  credit_note_too_long: { ar: 'الملاحظة طويلة جداً', en: 'The note is too long' },
  late_payment_unavailable: {
    ar: 'لم تعد السيارة متاحة لهذا الحجز',
    en: 'The car is no longer available for this booking',
  },
  'late_payment_unavailable.pickup_passed': {
    ar: 'مضى موعد الاستلام، فلم يعد الدفع لهذا الحجز ممكناً',
    en: 'The pickup time has passed, so this booking can no longer be paid',
  },
  nothing_due: { ar: 'لا يوجد مبلغ مستحق على هذا الحجز', en: 'Nothing is due on this booking' },
  payment_insufficient: {
    ar: 'المبلغ المدفوع أقل من المستحق',
    en: 'The amount paid is below the amount due',
  },
  invoice_not_found: { ar: 'لم نجد هذا المستند', en: "We couldn't find this document" },
  otp_code_invalid: { ar: 'الرمز من 4 إلى 8 أرقام', en: 'The code has 4 to 8 digits' },
};

const fieldCodes: Record<string, Text> = {
  required: { ar: 'هذا الحقل مطلوب', en: 'This field is required' },
  invalid: { ar: 'القيمة غير صحيحة', en: 'This value is not valid' },
  too_long: { ar: 'النص طويل جداً', en: 'This is too long' },
  too_short: { ar: 'النص قصير جداً', en: 'This is too short' },
  too_many: { ar: 'العدد كبير جداً', en: 'Too many' },
  out_of_range: { ar: 'القيمة خارج النطاق المسموح', en: 'This value is out of range' },
  unavailable: { ar: 'غير متاح', en: 'Not available' },
  in_past: { ar: 'الموعد في الماضي', en: 'This time is in the past' },
  in_future: { ar: 'التاريخ في المستقبل', en: 'This date is in the future' },
  duplicate: { ar: 'مكرر', en: 'Duplicate' },
  mismatch: { ar: 'القيمتان غير متطابقتين', en: "The values don't match" },
  below_last_reading: { ar: 'أقل من آخر قراءة مسجلة', en: 'Lower than the last recorded reading' },
  not_uploaded: { ar: 'لم يكتمل رفع الملف', en: 'The file upload did not finish' },
  format: { ar: 'الصيغة غير صحيحة', en: 'The format is not valid' },
  not_allowed: { ar: 'غير مسموح', en: 'Not allowed' },
  unknown: { ar: 'غير معروف', en: 'Unknown' },
  inactive: { ar: 'غير مفعّل', en: 'Inactive' },
  exceeds_total: { ar: 'أكبر من الإجمالي', en: 'More than the total' },
  not_combinable_with_promo: { ar: 'لا يمكن جمعه مع كود الخصم', en: "Can't be combined with a promo code" },
  outdated: { ar: 'نسخة قديمة', en: 'Outdated version' },
  not_found: { ar: 'غير موجود', en: 'Not found' },
  in_use: { ar: 'مستخدم في حجز آخر', en: 'Already used by another booking' },
  kind_invalid: { ar: 'هذا النوع غير مقبول', en: 'This kind is not accepted' },
  wrong_model: { ar: 'السيارة من طراز آخر', en: 'This car is another model' },
  before_from: { ar: 'يجب أن يأتي بعد تاريخ البداية', en: 'Must come after the start date' },
  before_month: { ar: 'يجب أن يأتي بعد شهر البداية', en: 'Must come after the start month' },
  before_handover: { ar: 'قبل وقت التسليم', en: 'Before the handover time' },
  deposit_not_allowed: {
    ar: 'تُدفع هذه السيارة كاملة عند الحجز',
    en: 'This car is paid in full when booking',
  },
  immutable: { ar: 'لا يمكن تغييره', en: "This can't be changed" },
};

/**
 * The variant of a problem its params select, looked up as "code.variant"
 * before the code itself: params.reason (car_unavailable "busy",
 * late_payment_unavailable "pickup_passed"), or for maintenance_conflict what
 * is in the way: a rental still out ("car_out") or only other maintenance.
 */
function variantOf(code: string, params: Readonly<Record<string, unknown>> | undefined): string | undefined {
  if (!params) return undefined;
  if (typeof params.reason === 'string') return params.reason;
  if (code === 'maintenance_conflict' && Array.isArray(params.conflicts) && params.conflicts.length > 0) {
    const cs = params.conflicts as readonly { kind?: unknown; status?: unknown }[];
    if (cs.some((c) => c.kind === 'booking' && c.status === 'active')) return 'car_out';
    if (cs.every((c) => c.kind === 'maintenance')) return 'maintenance';
  }
  return undefined;
}

/** Message for a problem code; `title` is the API's localized fallback, `params` the problem's params. */
export function problemMessage(
  locale: Locale,
  code: string | undefined,
  title?: string,
  params?: Readonly<Record<string, unknown>>,
): string {
  const variant = code ? variantOf(code, params) : undefined;
  const hit = (variant ? problems[`${code}.${variant}`] : undefined) ?? (code ? problems[code] : undefined);
  if (hit) return hit[locale];
  if (title) return title;
  return problems.internal?.[locale] ?? '';
}

/** Message for one field error, e.g. ("required") → "هذا الحقل مطلوب". */
export function fieldErrorMessage(
  locale: Locale,
  code: string,
  params?: Readonly<Record<string, unknown>>,
): string {
  const p = params ?? {};
  if (code === 'too_long' && typeof p.max === 'number') {
    return locale === 'ar' ? `الحد الأقصى ${p.max} حرفاً` : `At most ${p.max} characters`;
  }
  if (code === 'out_of_range' && typeof p.min === 'number' && typeof p.max === 'number') {
    return locale === 'ar' ? `بين ${p.min} و ${p.max}` : `Between ${p.min} and ${p.max}`;
  }
  if (code === 'below_last_reading' && typeof p.last === 'number') {
    return locale === 'ar' ? `أقل من آخر قراءة (${p.last})` : `Lower than the last reading (${p.last})`;
  }
  if (code === 'exceeds_total' && typeof p.max_baisa === 'number') {
    return locale === 'ar'
      ? `الحد الأقصى ${formatAmount(p.max_baisa)}`
      : `At most ${formatAmount(p.max_baisa)}`;
  }
  const hit = fieldCodes[code];
  return hit ? hit[locale] : (fieldCodes.invalid?.[locale] ?? code);
}

/** Lets an app add or override messages for its own codes. */
export function registerProblemMessages(extra: ProblemTexts): void {
  Object.assign(problems, extra);
}
export function registerFieldErrorMessages(extra: ProblemTexts): void {
  Object.assign(fieldCodes, extra);
}
